import { runInAction } from "mobx";
import { reportingBoundaryService } from "@/features/reporting-boundary/services/reporting-boundary.services";
import { organizationService } from "@/features/organization/services/organization.service";
import { dashboardFiltersStore } from "../stores/filters.store";
import { buildFrameworksFromReportingYears } from "../utils/reporting-years";
import { loadOrgHierarchyWithStatus } from "@/components/common/organization.utils";

const buildYearsFromRange = (startYear, endYear) => {
  if (!Number.isInteger(startYear) || !Number.isInteger(endYear)) return [];

  const minYear = Math.min(startYear, endYear);
  const maxYear = Math.max(startYear, endYear);
  const years = [];

  for (let year = minYear; year <= maxYear; year += 1) {
    years.push(year);
  }

  return years;
};

const buildYearsFromReportingYears = (reportingYears = []) => {
  const years = new Set();

  reportingYears.forEach((item) => {
    buildYearsFromRange(item?.startYear, item?.endYear).forEach((year) =>
      years.add(year),
    );
  });

  return [...years].sort((a, b) => a - b);
};

// Every loader takes the filters-store instance to populate; the dashboard
// singleton stays the default, Target Setup Overview passes its own.
export const loadBoundary = async (filtersStore = dashboardFiltersStore) => {
  filtersStore.setLoading("boundary", true);
  try {
    const [boundaryData, reportingYearsData, frameworksData] =
      await Promise.allSettled([
        reportingBoundaryService.getReportingBoundary(),
        reportingBoundaryService.getReportingYearByCompany(),
        reportingBoundaryService.getFrameworks(),
      ]);
    runInAction(() => {
      const boundary =
        boundaryData.status === "fulfilled"
          ? boundaryData.value?.reportingBoundary
          : null;
      const reportingYears =
        reportingYearsData.status === "fulfilled"
          ? (reportingYearsData.value?.reportingYears ?? [])
          : [];
      const reportingYearOptions = buildYearsFromReportingYears(reportingYears);

      filtersStore.setReportingYears(reportingYears);

      if (boundary) {
        filtersStore.setBoundary({
          startYear: boundary.startYear,
          endYear: boundary.endYear,
          frameworkCodes: boundary.frameworkCodes,
        });
      }

      if (reportingYearOptions.length > 0) {
        filtersStore.setAvailableYears(reportingYearOptions);
      }

      filtersStore.setAvailableFrameworks(
        buildFrameworksFromReportingYears({
          reportingYears,
          frameworkMetadata:
            frameworksData.status === "fulfilled"
              ? (frameworksData.value?.frameworks ?? [])
              : [],
        }),
      );
    });
  } finally {
    filtersStore.setLoading("boundary", false);
  }
};

export const loadOrgs = async (filtersStore = dashboardFiltersStore) => {
  filtersStore.setLoading("orgs", true);
  try {
    const tree = await loadOrgHierarchyWithStatus(organizationService);
    runInAction(() => {
      filtersStore.setOrgTree(tree || []);
    });
  } finally {
    filtersStore.setLoading("orgs", false);
  }
};

export const loadFilterOptions = (filtersStore = dashboardFiltersStore) =>
  Promise.allSettled([loadBoundary(filtersStore), loadOrgs(filtersStore)]);
